'use client';

import React, { useState, useEffect } from 'react';
import { PlayerInfoModal } from './player-info-modal'
import { Player } from '@repo/types';
import { X } from 'lucide-react';
import Image from 'next/image';
import { getDriverBackgroundColor, toMillionPrice, formatPrice } from '../../lib/utils';
import { useCompetitionContext } from "../../providers/CompetitionContext";

// Define the swap data new Driver Name is newID and old Driver Name is previousID
const swapData = [
  {
    weekId: 3,
    previousId: 93861,
    newId: 110659
  },
  {
    weekId: 3,
    newId: 93861,
    previousId: 110659
  },
];

interface DriverCardProps {
  driver?: Player;
  cardIndex?: number | undefined;
  type?: string;
  onRemove?: () => void;
  onSelect?: () => void;
  weekId: number;
}

export function DriverCard({
  driver,
  cardIndex,
  type,
  onRemove,
  onSelect,
  weekId
}: DriverCardProps) {
  const [showInfo, setShowInfo] = useState(false);
  const [swappedDriver, setSwappedDriver] = useState<Player | undefined>(driver);
  const { brand, competitionData } = useCompetitionContext();

  // Apply driver swapping logic
  useEffect(() => {
    if (!driver) {
      setSwappedDriver(undefined);
      return;
    }

    // Check if this driver's ID needs to be swapped based on weekId
    const swapEntry = swapData.find(swap => 
      swap.newId === driver.id && weekId < swap.weekId
    );

    if (swapEntry) {
      setSwappedDriver(competitionData.players.find((p: any) => p.id === swapEntry.previousId));
    } else {
      setSwappedDriver(driver);
    }
  }, [driver, weekId, competitionData]);

  if (!swappedDriver) {
    return (
      <button 
        onClick={onSelect}
        className="max-w-[160px] w-full h-36 border-2 border-dashed border-gray-300 rounded-lg p-4 hover:border-red-600 hover:bg-red-50 transition-colors"
      >
        <span className="text-lg font-semibold text-gray-700">{(type !== 'fantasyPick') ? `${(cardIndex || 0)+1}e - `: ``}Kies een coureur</span>
      </button>
    );
  }

  // Helper function to find points based on driver ID or statsId
  const getDriverPoints = () => {
    for (const stat of competitionData.stats) {
      if (stat.w === weekId) {
        try {
          const parsedStat = JSON.parse(stat.r);
          if (parsedStat.driver_id === swappedDriver.externalId) {
            return stat.p || 0;
          }
        } catch (e) {
          // Skip if parsing fails
          continue;
        }
      }
    }
    return 0;
  };

  return (
    <div className="relative max-w-[160px] w-full">
      <div className="bg-white rounded-lg shadow-md w-full overflow-hidden">
        <div className={`${getDriverBackgroundColor(swappedDriver.clubId)} p-1`}>
          <button 
            onClick={() => setShowInfo(true)}
            className="w-full focus:outline-none"
          >
            <Image
            src={`https://dpgnl.s3.eu-west-1.amazonaws.com/jerseys/formulaone/${swappedDriver.id}.png`}
            alt={swappedDriver.name}
            width={100}
            height={100}
            className="mx-auto w-auto h-auto"
            />
          </button>
        </div>
        <div className="p-1 bg-[#172449] text-white">
          <h3 className="text-sm font-semibold text-center">{swappedDriver.name}</h3>
        </div>
        <div className="p-1 border-t border-gray-200">
          <div className={`flex ${type === 'prediction' ? 'justify-between' : 'justify-center'} items-center`}>
            {(type === 'prediction' && swappedDriver.stats && swappedDriver.stats[0]?.results) && <span className="font-extra px-4">{swappedDriver.stats[0].results+`e`}</span>}
            {type === 'fantasyPick' && weekId < competitionData.deadlineInfo.deadlineWeek ? (
                  <><span className="font-extrabold px-2">
                    {`${getDriverPoints()} punten`}
                  </span></>
                ) : (
                  <span className="font-extrabold">
                    {cardIndex !== undefined
                      ? swappedDriver.stats && swappedDriver.stats[0]?.results ? `${swappedDriver.stats[0].points} punten` : `${(cardIndex || 0) + 1}e`
                      : `${toMillionPrice(swappedDriver.value)/1_000_000}M`
                    }
                  </span>
                )}
          </div>
        </div>
      </div>
      {onRemove && (
          <button
            id={`d_${swappedDriver.id}`}
            onClick={onRemove}
            className={`absolute top-0 right-0 transform translate-x-1/2 -translate-y-1/2 p-1 bg-${brand}-header text-white rounded-full hover:bg-red-700 transition-colors z-10`}
          >
            <X size={16} />
          </button>
        )}

      {swappedDriver && (
        <PlayerInfoModal
          isOpen={showInfo}
          onClose={() => setShowInfo(false)}
          onRemove={onRemove || (() => {})}
          driver={swappedDriver}
          weekId={weekId}
        />
      )}
    </div>
  );
}